import React, { useState, useEffect, useRef } from "react";
import {
  Input, Select, Button, Box, FormControl, FormLabel,
  Flex, Heading, SimpleGrid, Text, Image, Link,
  Spinner,
} from "@chakra-ui/react";
import { addCompany, updateCompany, uploadCompanyFile, getCompanyFile } from "../../../Http/Index";
import { Company } from "../../../Interfaces/Company";

interface AddDataProps {
  company?: Company | null;
  onClose: () => void;
  onSuccess: () => void;
}

const emptyForm = {
  companyName: "",
  subscriptionType: "",
  category: "",
  webContext: "",
  emailPattern: "",
  addBenAllowed: undefined as boolean | undefined,
  noOfBenAllowed: undefined as number | undefined,
  dateOfPartnership: new Date().toISOString().split('T')[0],
  contractRenewalDate: "",
  logo: "",
  tpaDocument: "",
};

const UploadField = ({
  label,
  accept,
  currentUrl,
  previewUrl,
  uploading,
  onFileChange,
}: {
  label: string;
  accept: string;
  currentUrl: string;
  previewUrl?: string | null;
  uploading: boolean;
  onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const isImage = accept.includes("image");

  return (
    <FormControl>
      <FormLabel>{label}</FormLabel>
      <Flex
        direction="column"
        align="center"
        justify="center"
        border="2px dashed"
        borderColor="gray.300"
        borderRadius="md"
        bg="gray.50"
        p={4}
        gap={2}
        cursor="pointer"
        onClick={() => inputRef.current?.click()}
        _hover={{ borderColor: "teal.400", bg: "teal.50" }}
        minH="100px"
      >
        {uploading ? (
          <Spinner color="teal.500" />
        ) : currentUrl ? (
          isImage && previewUrl ? (
            <Image src={previewUrl} alt={label} maxH="80px" objectFit="contain" />
          ) : (
            <Link href={currentUrl} target="_blank" color="teal.600" fontSize="sm" onClick={(e) => e.stopPropagation()}>
              View uploaded document
            </Link>
          )
        ) : (
          <Text fontSize="sm" color="gray.500">Click to upload {label}</Text>
        )}
        {currentUrl && (
          <Text fontSize="xs" color="gray.400">Click to replace</Text>
        )}
      </Flex>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        style={{ display: "none" }}
        onChange={onFileChange}
      />
    </FormControl>
  );
};

const AddData: React.FC<AddDataProps> = ({ company, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({ ...emptyForm });
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingTpa, setUploadingTpa] = useState(false);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    if (company) {
      setFormData({
        companyName: company.companyName || "",
        subscriptionType: company.subscriptionType || "",
        category: (company as any).category || "",
        webContext: (company as any).webContext || "",
        emailPattern: (company as any).emailPattern || "",
        addBenAllowed: (company as any).addBenAllowed,
        noOfBenAllowed: (company as any).noOfBenAllowed,
        dateOfPartnership: (company as any).dateOfPartnership || "",
        contractRenewalDate: (company as any).contractRenewalDate || "",
        logo: (company as any).logo || "",
        tpaDocument: (company as any).tpaDocument || "",
      });
    } else {
      setFormData({ ...emptyForm });
    }
  }, [company]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
    field: "logo" | "tpaDocument"
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!formData.companyName) {
      alert("Please enter the company name before uploading files.");
      return;
    }

    const ext = file.name.slice(file.name.lastIndexOf('.'));
    const docFolder = field === "logo" ? "partner-companies-logo" : "tpadocument";
    const docName = field === "logo"
      ? `${formData.companyName}-logo`
      : `${formData.companyName}-tpa`;

    field === "logo" ? setUploadingLogo(true) : setUploadingTpa(true);
    try {
      const response = await uploadCompanyFile(file, docFolder, docName);
      const path: string = response?.data?.data?.url;
      if (path) {
        setFormData((prev) => ({ ...prev, [field]: path }));
        if (field === "logo") {
          const finalDocName = docName.includes('.') ? docName : `${docName}${ext}`;
          try {
            const blob = await getCompanyFile(docFolder, finalDocName);
            setLogoPreviewUrl(URL.createObjectURL(new Blob([blob.data], { type: blob.headers['content-type'] as string | undefined })));
          } catch { }
        }
      } else {
        alert(`Failed to upload ${field}`);
      }
    } catch {
      alert(`Error uploading ${field}`);
    } finally {
      field === "logo" ? setUploadingLogo(false) : setUploadingTpa(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.companyName) {
      alert("Please fill in the company name.");
      return;
    }
    if (!formData.subscriptionType) {
      alert("Please fill in the subscription type.");
      return;
    }
    if (formData.noOfBenAllowed && formData.noOfBenAllowed < 0) {
      alert("Please enter a valid number of beneficiaries allowed.");
      return;
    }

    try {
      if (company?._id) {
        const response = await updateCompany(company._id, formData);
        if (response?.data?.status === 200 || response?.status === 200) {
          alert("Company updated successfully");
          onSuccess();
          onClose();
        } else {
          alert("Error occurred while updating company");
        }
      } else {
        const payload = {
          ...formData,
          dateOfPartnership: formData.dateOfPartnership || new Date().toISOString().split('T')[0],
          isActivePartnership: true,
          isDeleted: false
       };
        const response = await addCompany(payload);
        if (response?.data?.status === 201) {
          alert(response?.data?.message);
          onSuccess();
          onClose();
        } else {
          alert("Error occurred while creating company");
        }
      }
    } catch (error: any) {
      alert(error?.response?.data?.message || "Error occurred while making request");
    }
  };

  return (
    <Box bg="gray.50" minH="60vh" p={6}>
      <Flex justify="space-between" align="center" mb={6}>
        <Heading size="md" color="teal.600">
          {company ? "Edit Company" : "Add Company"}
        </Heading>
        <Button variant="outline" onClick={onClose}>Back to List</Button>
      </Flex>

      <Box bg="white" p={6} borderWidth={1} borderRadius="lg" shadow="sm">
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>

          <FormControl isRequired>
          <FormLabel>Company Name</FormLabel>
          <Input
            placeholder="Enter Company Name"
            name="companyName"
            value={formData.companyName}
            onChange={handleChange}
            bg="gray.50" 
          />

        </FormControl>

        <FormControl isRequired>
          <FormLabel>Subscription Type</FormLabel>
          <Select
            name="subscriptionType"
            value={formData.subscriptionType}
            onChange={handleChange}
            bg="gray.50"
          >
            <option value="">Select Type</option>
            <option value="BASIC">Basic</option>
            <option value="HOSPITAL">Hospital</option>
            <option value="INSURANCE">Insurance</option>
            <option value="ENTERPRISE">Enterprise</option>
          </Select>

        </FormControl>

          <FormControl>
            <FormLabel>Category</FormLabel>
            <Select name="category" value={formData.category} onChange={handleChange} bg="gray.50">
              <option value="">Select Category</option>
              <option value="ITES">ITES</option>
              <option value="FIELD_SALES_MFG">Field, Sales & Mfg</option>
              <option value="BFSI">BFSI</option>
              <option value="INSURANCE">Insurance</option>
              <option value="OTHERS">Others</option>
            </Select>
          </FormControl>

          <FormControl>
            <FormLabel>Date of Partnership</FormLabel>
            <Input
             type="date"
             name="dateOfPartnership"
             value={formData.dateOfPartnership}
             onChange={handleChange}
             bg="gray.50"
            />
          </FormControl>

          <FormControl>
            <FormLabel>Contract Renewal Date</FormLabel>
            <Input type="date" name="contractRenewalDate" value={formData.contractRenewalDate} onChange={handleChange} bg="gray.50" />
          </FormControl>

          <FormControl>
            <FormLabel>Web Context</FormLabel>
          <Input
            placeholder="Enter Web Context"
            name="webContext"
            value={formData.webContext}
            onChange={handleChange}
            bg="gray.50"
          />
        </FormControl>

        <FormControl>
          <FormLabel>Email Pattern</FormLabel>
          <Input
            placeholder="Enter Email Pattern"
            name="emailPattern"
            value={formData.emailPattern}
            onChange={handleChange}
            bg="gray.50"
          />
          </FormControl>

          <FormControl>
            <FormLabel>Add Beneficiary Allowed</FormLabel>
            <Select
              name="addBenAllowed"
              placeholder="Select option"
              value={formData.addBenAllowed !== undefined ? String(formData.addBenAllowed) : ""}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  addBenAllowed: e.target.value === "true" ? true : e.target.value === "false" ? false : undefined,
                })
              }
              bg="gray.50"
            >
              <option value="true">Yes</option>
              <option value="false">No</option>
            </Select>
          </FormControl>

          <FormControl>
            <FormLabel>No. of Beneficiaries Allowed</FormLabel>
            <Input
              type="number"
              placeholder="Enter No. of Beneficiaries Allowed"
              name="noOfBenAllowed"
              value={formData.noOfBenAllowed ?? ""}
              onChange={(e) =>
                setFormData({
                 ...formData,
                 noOfBenAllowed: e.target.value ? parseInt(e.target.value) : undefined 
                })
              }
              bg="gray.50"
            />
          </FormControl>

          <UploadField
            label="Company Logo"
            accept="image/*"
            currentUrl={formData.logo}
            previewUrl={logoPreviewUrl}
            uploading={uploadingLogo}
            onFileChange={(e) => handleFileUpload(e, "logo")}
          />

          <UploadField
            label="TPA Document"
            accept=".pdf,.doc,.docx"
            currentUrl={formData.tpaDocument}
            uploading={uploadingTpa}
            onFileChange={(e) => handleFileUpload(e, "tpaDocument")}
          />

        </SimpleGrid>
      </Box>

      <Flex mt={6} justify="flex-end" bg="white" p={4} borderRadius="lg" shadow="sm" borderWidth={1}>
        <Button colorScheme="green" size="lg" px={10} onClick={handleSubmit} isDisabled={uploadingLogo || uploadingTpa}>
          {company ? "Save Updates" : "Save Company"}
        </Button>
      </Flex>
    </Box>
  );
};

export default AddData;
